import * as FC from "@fluentui/react-components";
import { useState, useEffect, useContext } from "react";
import { TableDef } from "../table_def";
import { idString } from "../db_utils";
import { Context } from "../context";

export const ModelTurbineDef = new TableDef("model_turbine", {
    seq: {
        label: "编号",
    },
    manu: {
        label: "厂商",
        display: (item) => item.manu?.name,
    },
    power: {
        label: "功率(MW)",
        display: (item) => item.power?.toString(),
    },
    description: {
        label: "备注",
    },
});

const model_turbine_searchable_columns = [
    "seq",
    "manu.name",
    "power",
    // "description"
];
const applyModelTurbineFilter = (modelTurbines, filter) => {
    if (!filter || typeof filter !== "string" || !filter.trim()) {
        return modelTurbines;
    }

    const filterLower = filter.toLowerCase();
    return modelTurbines.filter((model) =>
        model_turbine_searchable_columns.some((column) => {
            const value = column.includes(".")
                ? column.split(".").reduce((obj, key) => obj?.[key], model)
                : model[column];
            return value?.toString().toLowerCase().includes(filterLower);
        })
    );
};

/**
 * Loads turbine models, propagating transport/auth failures to the caller.
 *
 * @param apiClient       the shared API client
 * @param options.filter  client-side text filter, or null
 * @param options.fieldId when set, only models belonging to that field are returned. A
 *                        turbine model is owned by exactly one field
 *                        (ModelTurbine.FieldItemId) and the turbine write path rejects a
 *                        cross-field pair with TurbineModelFieldMismatch, so any picker
 *                        bound to a known field MUST scope the list here.
 */
export const fetchModelTurbines = async (
    apiClient,
    { filter = null, fieldId = null } = {}
) => {
    const params = {};
    if (fieldId !== null && fieldId !== undefined && fieldId !== "") {
        params.fieldId = fieldId;
    }

    const response = await apiClient.getModelTurbines(params);
    return applyModelTurbineFilter(response || [], filter);
};

/**
 * Failure-swallowing wrapper kept for the model catalogue grid, which treats an error as
 * an empty page. Do NOT use it where "empty" and "failed" must be told apart — a picker
 * that renders a load failure as "this field has no models" sends the user to create
 * duplicates of records that already exist.
 */
export const listModelTurbine = async (apiClient, filter = null, fieldId = null) => {
    try {
        return await fetchModelTurbines(apiClient, { filter, fieldId });
    } catch (error) {
        console.error("Error loading model turbines:", error);
        if (error.message && error.message.includes('404')) {
            console.warn("ModelTurbine API not implemented. Using empty list.");
        }
        return [];
    }
};

/**
 * Model codes were normalised to "厂商 功率MW" on 2026-08-14, so for those rows the code is
 * already self-describing and appending the manufacturer again reads as "金风 2.5MW (金风 ·
 * 2.5MW)". Rows created before that — or by anyone typing a bare code — still need the suffix
 * to be identifiable in one global list, so it is added only when the code omits the maker.
 */
export const modelTurbineOptionText = (item) => {
    const seq = `${item?.seq ?? ""}`;
    const manu = item?.manu?.name;
    if (manu && seq.includes(manu)) {
        return seq;
    }

    const power = item?.power ? `${item.power}MW` : null;
    const suffix = [manu, power].filter(Boolean).join(" · ");
    return suffix ? `${seq} (${suffix})` : seq;
};

/**
 * Turbine-model picker. Models are a global catalogue, so a fieldId supplied by an older
 * caller is deliberately ignored.
 *
 * @param {import("@fluentui/react-components").DropdownProps} props
 */
export const ModelTurbineDropdown = ({ fieldId: _legacyFieldId, placeholder, ...props }) => {
    void _legacyFieldId;
    const { apiClient } = useContext(Context);
    const [modelTurbines, setModelTurbines] = useState([]);
    // loading | ready | error — "no models in this field" and "the list failed to load"
    // are different problems with different fixes, so they get different states.
    const [status, setStatus] = useState("loading");

    useEffect(() => {
        if (!apiClient) {
            return undefined;
        }

        let cancelled = false;
        setStatus("loading");
        fetchModelTurbines(apiClient)
            .then((models) => {
                if (cancelled) {
                    return;
                }
                setModelTurbines(models);
                setStatus("ready");
            })
            .catch((error) => {
                if (cancelled) {
                    return;
                }
                console.error("Failed to load model turbines:", error);
                setModelTurbines([]);
                setStatus("error");
            });

        return () => {
            cancelled = true;
        };
    }, [apiClient]);

    const isEmpty = status === "ready" && modelTurbines.length === 0;
    const resolvedPlaceholder =
        status === "loading"
            ? "正在加载风机型号..."
            : status === "error"
            ? "风机型号加载失败"
            : isEmpty
            ? "暂无风机型号"
            : placeholder ?? "请选择风机型号";

    return (
        <>
            <FC.Dropdown
                placeholder={resolvedPlaceholder}
                disabled={status !== "ready" || isEmpty}
                {...props}
            >
                {modelTurbines.map((item) => (
                    <FC.Option
                        key={idString(item.id)}
                        value={idString(item.id)}
                        text={modelTurbineOptionText(item)}
                    >
                        {modelTurbineOptionText(item)}
                    </FC.Option>
                ))}
            </FC.Dropdown>
            {isEmpty && (
                <FC.Text size={200} style={{ color: FC.tokens.colorPaletteRedForeground1 }}>
                    系统中还没有风机型号，请先在「风机型号」中创建。
                </FC.Text>
            )}
            {status === "error" && (
                <FC.Text size={200} style={{ color: FC.tokens.colorPaletteRedForeground1 }}>
                    风机型号列表加载失败，请刷新页面重试；请勿在确认前重复创建型号。
                </FC.Text>
            )}
        </>
    );
};
